import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Building2, ArrowRight, Check, Globe } from 'lucide-react';
import { api } from './authStore';
import { planService, Plan } from '../../services/plan.service';
import { countryService, Country } from '../../services/country.service';

const registerSchema = z.object({
  // Tenant/Firm Information
  firmName: z.string().min(1, 'Firm name is required').max(100, 'Firm name too long'),
  subdomain: z.string()
    .min(2, 'Subdomain must be at least 2 characters')
    .max(50, 'Subdomain too long')
    .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/, 'Subdomain can only contain lowercase letters, numbers, and hyphens'),
  country: z.string().min(1, 'Please select a country'),
  
  // Admin User Information
  firstName: z.string().min(1, 'First name is required').max(50, 'First name too long'),
  lastName: z.string().min(1, 'Last name is required').max(50, 'Last name too long'),
  email: z.string().email('Please enter a valid email address'),
  password: z.string()
    .min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),

  // Plan Selection
  plan: z.string().min(1, 'Please select a plan'),
  
  // Terms and Conditions
  acceptTerms: z.boolean().refine(val => val === true, 'You must accept the terms and conditions'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type RegisterFormData = z.infer<typeof registerSchema>;

export const RegisterPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [plansLoading, setPlansLoading] = useState(true);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      plan: '',
      country: 'canada',
      acceptTerms: false,
    },
    shouldFocusError: false, // Disable automatic focus to prevent scrollIntoView errors
  });

  const watchedSubdomain = watch('subdomain');

  // Fetch available countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setCountriesLoading(true);
        const response = await countryService.getSupportedCountries();
        setCountries(response.data);
        
        // Set default country to Canada
        setValue('country', 'canada');
      } catch (error: any) {
        console.error('Error fetching countries:', error);
        toast.error('Failed to load countries. Please try again later.');
      } finally {
        setCountriesLoading(false);
      }
    };

    fetchCountries();
  }, [setValue]);

  // Fetch available plans on mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setPlansLoading(true);
        const response = await planService.getPlansForPurchase();
        const activePlans = response.data.filter(plan => plan.isActive);
        
        // Sort by sequence number
        activePlans.sort((a, b) => a.sequenceNumber - b.sequenceNumber);
        
        setPlans(activePlans);
        
        // Set default plan to the first one or the one with popular flag
        if (activePlans.length > 0) {
          const defaultPlan = activePlans[0];
          setSelectedPlan(defaultPlan._id);
          setValue('plan', defaultPlan._id);
        }
      } catch (error: any) {
        console.error('Error fetching plans:', error);
        toast.error('Failed to load plans. Please try again later.');
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPlans();
  }, [setValue]);

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    try {
      const response = await api.post('/auth/register-tenant', {
        tenant: {
          name: data.firmName,
          subdomain: data.subdomain.toLowerCase(),
          plan: data.plan,
          country: data.country,
        },
        admin: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          password: data.password,
        },
      });

      if (response.data.success) {
        toast.success('Registration successful! Welcome to ERPCA.');
        navigate('/login', { 
          state: { 
            message: 'Registration successful! Please login with your credentials.',
            tenant: data.subdomain,
            email: data.email
          } 
        });
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      
      // Handle validation errors from backend
      if (error.response?.status === 400) {
        const errorData = error.response?.data;
        if (errorData?.error?.details && Array.isArray(errorData.error.details)) {
          // Backend validation error with field details
          const details = errorData.error.details as any[];
          if (details.length > 0) {
            const fieldError = details[0];
            toast.error(`${fieldError.field}: ${fieldError.message}`);
          } else {
            toast.error(errorData.error.message || 'Validation failed. Please check your input.');
          }
        } else {
          toast.error(errorData?.error?.message || errorData?.message || 'Validation failed. Please try again.');
        }
      } else if (error.response?.data?.error?.message) {
        toast.error(error.response.data.error.message);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pastel-blue via-white to-pastel-green">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-primary-500" />
          </div>
          <h1 className="text-4xl font-display font-bold text-primary-500 mb-2">
            Start Your ERPCA Journey
          </h1>
          <p className="text-gray-600 font-body text-lg">
            Join thousands of accounting firms managing their practice with ERPCA
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Form */}
            <div className="lg:col-span-1 bg-white/90 backdrop-blur-sm rounded-3xl shadow-soft-lg p-8 border border-white/20">
              <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">
                Create Your Account
              </h2>

              {/* Firm Information */}
              <div className="space-y-6 mb-8">
                <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                  Firm Information
                </h3>
                
                <div className="form-group">
                  <label htmlFor="firmName" className="form-label">
                    Firm Name
                  </label>
                  <input
                    {...register('firmName')}
                    type="text"
                    id="firmName"
                    className="input"
                    placeholder="e.g., Smith & Associates CPA"
                  />
                  {errors.firmName && (
                    <p className="form-error">{errors.firmName.message}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="country" className="form-label">
                    <Globe className="w-4 h-4 inline mr-2" />
                    Country
                  </label>
                  <select
                    {...register('country')}
                    id="country"
                    className="input"
                    disabled={countriesLoading}
                  >
                    {countriesLoading ? (
                      <option value="">Loading countries...</option>
                    ) : (
                      <>
                        <option value="">Select a country</option>
                        {countries.map((country) => (
                          <option key={country.value} value={country.value}>
                            {country.label}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  {errors.country && (
                    <p className="form-error">{errors.country.message}</p>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    This will set your currency, taxation rules, and regional settings
                  </p>
                </div>

                <div className="form-group">
                  <label htmlFor="subdomain" className="form-label">
                    Subdomain
                  </label>
                  <div className="relative">
                    <input
                      {...register('subdomain')}
                      type="text"
                      id="subdomain"
                      className="input pr-32"
                      placeholder="your-firm"
                      onChange={(e) => {
                        const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                        setValue('subdomain', value);
                      }}
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">
                      .erpca.ca
                    </div>
                  </div>
                  {watchedSubdomain && (
                    <p className="text-sm text-gray-600 mt-1">
                      Your firm will be accessible at: <strong>{watchedSubdomain}.erpca.ca</strong>
                    </p>
                  )}
                  {errors.subdomain && (
                    <p className="form-error">{errors.subdomain.message}</p>
                  )}
                </div>
              </div>

              {/* Admin User Information */}
              <div className="space-y-6 mb-8">
                <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                  Administrator Account
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label htmlFor="firstName" className="form-label">
                      First Name
                    </label>
                    <input
                      {...register('firstName')}
                      type="text"
                      id="firstName"
                      className="input"
                      placeholder="John"
                    />
                    {errors.firstName && (
                      <p className="form-error">{errors.firstName.message}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="lastName" className="form-label">
                      Last Name
                    </label>
                    <input
                      {...register('lastName')}
                      type="text"
                      id="lastName"
                      className="input"
                      placeholder="Smith"
                    />
                    {errors.lastName && (
                      <p className="form-error">{errors.lastName.message}</p>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email Address
                  </label>
                  <input
                    {...register('email')}
                    type="email"
                    id="email"
                    className="input"
                    placeholder="john@smithcpa.com"
                  />
                  {errors.email && (
                    <p className="form-error">{errors.email.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label htmlFor="password" className="form-label">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        {...register('password')}
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        className="input pr-12"
                        placeholder="Create a strong password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="form-error">{errors.password.message}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword" className="form-label">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        {...register('confirmPassword')}
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        className="input pr-12"
                        placeholder="Confirm your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p className="form-error">{errors.confirmPassword.message}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Terms and Submit */}
              <div className="space-y-6">
                <div className="flex items-start space-x-3">
                  <input
                    {...register('acceptTerms')}
                    type="checkbox"
                    id="acceptTerms"
                    className="mt-1 w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="acceptTerms" className="text-sm text-gray-600">
                    I agree to the{' '}
                    <Link to="/terms" className="text-primary-500 hover:text-primary-600 underline">
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="text-primary-500 hover:text-primary-600 underline">
                      Privacy Policy
                    </Link>
                  </label>
                </div>
                {errors.acceptTerms && (
                  <p className="form-error">{errors.acceptTerms.message}</p>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full btn-primary py-3 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Creating Your Account...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <span>Start Free Trial</span>
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </div>
                  )}
                </button>

                <p className="text-center text-sm text-gray-600">
                  Already have an account?{' '}
                  <Link to="/login" className="text-primary-500 hover:text-primary-600 font-medium">
                    Sign in here
                  </Link>
                </p>
              </div>
            </div>

            {/* Right Column - Plan Selection (spans 2 columns) */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-soft-lg p-8 border border-white/20">
                <h2 className="text-2xl font-display font-bold text-gray-900 mb-6 text-center">
                  Choose Your Plan
                </h2>

                {plansLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className="ml-3 text-gray-600">Loading plans...</span>
                  </div>
                ) : plans.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-600">No plans available at the moment.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {plans.map((plan, index) => {
                      // Helper function to format duration
                      const getDurationText = (duration: string) => {
                        switch (duration) {
                          case 'monthly': return '/month';
                          case 'quarterly': return '/quarter';
                          case 'yearly': return '/year';
                          case 'lifetime': return ' (one-time)';
                          default: return '/month';
                        }
                      };

                      // Helper function to format features
                      const getFormattedFeatures = () => {
                        const features = [];
                        
                        // Standard features for all plans
                        features.push(`Up to ${plan.maxUsers} users`);
                        features.push(`Up to ${plan.maxClients} clients`);
                        features.push('Client Management');
                        features.push('Task Management');
                        features.push('Time Tracking');
                        features.push('Reporting');
                        features.push('Email Support');
                        
                        // Add only enabled feature flags
                        if (plan.featureFlags.eSign) features.push('E-Signature');
                        if (plan.featureFlags.payroll) features.push('Payroll Management');
                        if (plan.featureFlags.biometricImport) features.push('Biometric Import');
                        if (plan.featureFlags.advancedReporting) features.push('Advanced Reporting');
                        if (plan.featureFlags.apiAccess) features.push('API Access');
                        if (plan.featureFlags.multiCurrency) features.push('Multi Currency');
                        if (plan.featureFlags.customBranding) features.push('Custom Branding');
                        if (plan.featureFlags.prioritySupport) features.push('Priority Support');
                        
                        return features;
                      };

                      const features = getFormattedFeatures();
                      // Show recommended badge if plan is marked as recommended, otherwise mark middle plan as popular
                      const isRecommended = plan.isRecommended;
                      const isPopular = !isRecommended && plans.length >= 3 && index === Math.floor(plans.length / 2);
                      const isHighlighted = isRecommended || isPopular;

                      return (
                        <div
                          key={plan._id}
                          className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all duration-200 flex flex-col ${
                            selectedPlan === plan._id
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-gray-300'
                          } ${isHighlighted ? 'ring-2 ring-secondary-500 ring-opacity-50 scale-105' : ''}`}
                          onClick={() => {
                            setSelectedPlan(plan._id);
                            setValue('plan', plan._id);
                          }}
                        >
                          {isRecommended && (
                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                              <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                                Recommended
                              </span>
                            </div>
                          )}
                          {isPopular && !isRecommended && (
                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                              <span className="bg-secondary-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                                Most Popular
                              </span>
                            </div>
                          )}
                          
                          <div className="text-center mb-4">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.displayName}</h3>
                            <p className="text-sm text-gray-600 mb-3">{plan.description}</p>
                            <div className="mb-4">
                              <span className="text-3xl font-bold text-gray-900">${plan.cost}</span>
                              <span className="text-sm text-gray-600">{getDurationText(plan.duration)}</span>
                            </div>
                          </div>

                          <ul className="space-y-2 flex-grow">
                            {features.map((feature, idx) => (
                              <li key={idx} className="flex items-start text-xs text-gray-600">
                                <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>

                          <input
                            {...register('plan')}
                            type="radio"
                            value={plan._id}
                            className="sr-only"
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                  <p className="text-sm text-blue-800">
                    <strong>14-day free trial</strong> included with all plans. No credit card required to start.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
